import React, { useState, useRef, useEffect, useCallback } from "react";
import { Editor } from "@tiptap/react";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import {
  BsChevronUp,
  BsChevronDown,
  BsX,
} from "react-icons/bs";
import "./FindReplaceBar.css";

interface FindReplaceBarProps {
  editor: Editor;
  onClose: () => void;
  showReplace?: boolean;
}

interface SearchMatch {
  from: number;
  to: number;
}

const searchPluginKey = new PluginKey("findReplace");

function createSearchPlugin() {
  return new Plugin({
    key: searchPluginKey,
    state: {
      init() {
        return { decorations: DecorationSet.empty, matches: [] as SearchMatch[], currentIndex: -1 };
      },
      apply(tr, prev) {
        const meta = tr.getMeta(searchPluginKey);
        if (meta) {
          return meta;
        }
        // Map decorations through document changes
        if (tr.docChanged) {
          return {
            ...prev,
            decorations: prev.decorations.map(tr.mapping, tr.doc),
          };
        }
        return prev;
      },
    },
    props: {
      decorations(state) {
        return searchPluginKey.getState(state)?.decorations || DecorationSet.empty;
      },
    },
  });
}

const FindReplaceBar: React.FC<FindReplaceBarProps> = ({
  editor,
  onClose,
  showReplace: initialShowReplace = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [replaceTerm, setReplaceTerm] = useState("");
  const [showReplace, setShowReplace] = useState(initialShowReplace);
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  const [useRegex, setUseRegex] = useState(false);
  const [regexError, setRegexError] = useState(false);
  const [matches, setMatches] = useState<SearchMatch[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const pluginAddedRef = useRef(false);

  // Register the search plugin on mount
  useEffect(() => {
    if (!editor || pluginAddedRef.current) return;

    // Check if plugin already exists
    const existingPlugin = editor.state.plugins.find(
      (p) => p.spec.key === searchPluginKey
    );
    if (!existingPlugin) {
      const plugin = createSearchPlugin();
      const { state } = editor;
      const newState = state.reconfigure({
        plugins: [...state.plugins, plugin],
      });
      editor.view.updateState(newState);
    }
    pluginAddedRef.current = true;

    return () => {
      // Clear decorations on unmount
      if (editor && !editor.isDestroyed) {
        const { tr } = editor.state;
        tr.setMeta(searchPluginKey, {
          decorations: DecorationSet.empty,
          matches: [],
          currentIndex: -1,
        });
        editor.view.dispatch(tr);
      }
    };
  }, [editor]);

  // Focus search input on mount
  useEffect(() => {
    setTimeout(() => searchInputRef.current?.focus(), 100);
  }, []);

  // Pre-fill search with selected text
  useEffect(() => {
    if (editor) {
      const { from, to } = editor.state.selection;
      if (from !== to) {
        const selectedText = editor.state.doc.textBetween(from, to, " ");
        if (selectedText.length <= 100) {
          setSearchTerm(selectedText);
        }
      }
    }
  }, [editor]);

  const performSearch = useCallback(
    (term: string, caseMatch: boolean) => {
      if (!editor || !term) {
        // Clear decorations
        const { tr } = editor.state;
        tr.setMeta(searchPluginKey, {
          decorations: DecorationSet.empty,
          matches: [],
          currentIndex: -1,
        });
        editor.view.dispatch(tr);
        setMatches([]);
        setCurrentIndex(-1);
        return;
      }

      const { doc } = editor.state;
      const text = doc.textContent;

      // Build the matcher. Regex mode uses the term as-is; otherwise the term is
      // escaped and optionally wrapped in \b for whole-word matching.
      const flags = caseMatch ? "g" : "gi";
      let regex: RegExp;
      try {
        if (useRegex) {
          regex = new RegExp(term, flags);
        } else {
          const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
          regex = new RegExp(wholeWord ? `\\b${escaped}\\b` : escaped, flags);
        }
        setRegexError(false);
      } catch {
        // Invalid regex — surface the error and show no matches (don't throw).
        setRegexError(true);
        const { tr } = editor.state;
        tr.setMeta(searchPluginKey, {
          decorations: DecorationSet.empty,
          matches: [],
          currentIndex: -1,
        });
        editor.view.dispatch(tr);
        setMatches([]);
        setCurrentIndex(-1);
        return;
      }

      // Collect (index, length) matches from the plain text content.
      const raw: { index: number; length: number }[] = [];
      let m: RegExpExecArray | null;
      let safety = 0;
      while ((m = regex.exec(text)) !== null) {
        raw.push({ index: m.index, length: m[0].length });
        if (m[0].length === 0) regex.lastIndex++; // avoid zero-width infinite loop
        if (++safety > 100000) break;
      }

      // Convert each text offset to document positions.
      const foundMatches: SearchMatch[] = [];
      for (const { index: idx, length } of raw) {
        let textOffset = 0;
        let fromPos = -1;
        let toPos = -1;
        const endIdx = idx + length;

        doc.descendants((node, pos) => {
          if (fromPos >= 0 && toPos >= 0) return false;
          if (node.isText && node.text) {
            const nodeEnd = textOffset + node.text.length;
            if (fromPos < 0 && idx >= textOffset && idx < nodeEnd) {
              fromPos = pos + (idx - textOffset);
            }
            if (toPos < 0 && endIdx > textOffset && endIdx <= nodeEnd) {
              toPos = pos + (endIdx - textOffset);
            }
            textOffset = nodeEnd;
          }
        });

        if (fromPos >= 0 && toPos >= 0) {
          foundMatches.push({ from: fromPos, to: toPos });
        }
      }

      // Create decorations
      const decorations = foundMatches.map((match, i) => {
        const className =
          i === (currentIndex >= 0 ? currentIndex : 0)
            ? "find-replace-current-match"
            : "find-replace-match";
        return Decoration.inline(match.from, match.to, {
          class: className,
        });
      });

      const { tr } = editor.state;
      tr.setMeta(searchPluginKey, {
        decorations: DecorationSet.create(doc, decorations),
        matches: foundMatches,
        currentIndex: foundMatches.length > 0 ? Math.max(0, currentIndex) : -1,
      });
      editor.view.dispatch(tr);

      setMatches(foundMatches);
      if (foundMatches.length > 0 && currentIndex < 0) {
        setCurrentIndex(0);
      } else if (foundMatches.length === 0) {
        setCurrentIndex(-1);
      }
    },
    [editor, currentIndex, useRegex, wholeWord]
  );

  // Re-search when the term or any search option changes (debounced for large docs)
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(searchTerm, caseSensitive);
    }, searchTerm.length <= 2 ? 300 : 150);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, caseSensitive, wholeWord, useRegex]);

  // Re-search when document changes
  useEffect(() => {
    if (!editor) return;
    const handler = () => {
      if (searchTerm) {
        performSearch(searchTerm, caseSensitive);
      }
    };
    editor.on("update", handler);
    return () => {
      editor.off("update", handler);
    };
  }, [editor, searchTerm, caseSensitive, performSearch]);

  const updateCurrentHighlight = useCallback(
    (newIndex: number) => {
      if (!editor || matches.length === 0) return;

      const { doc } = editor.state;
      const decorations = matches.map((match, i) => {
        const className =
          i === newIndex
            ? "find-replace-current-match"
            : "find-replace-match";
        return Decoration.inline(match.from, match.to, {
          class: className,
        });
      });

      const { tr } = editor.state;
      tr.setMeta(searchPluginKey, {
        decorations: DecorationSet.create(doc, decorations),
        matches,
        currentIndex: newIndex,
      });
      editor.view.dispatch(tr);
      setCurrentIndex(newIndex);

      // Scroll to match
      const match = matches[newIndex];
      if (match) {
        editor.commands.setTextSelection(match.from);
        const dom = editor.view.domAtPos(match.from);
        if (dom.node instanceof HTMLElement) {
          dom.node.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (dom.node.parentElement) {
          dom.node.parentElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    },
    [editor, matches]
  );

  const handleNext = useCallback(() => {
    if (matches.length === 0) return;
    const newIndex = (currentIndex + 1) % matches.length;
    updateCurrentHighlight(newIndex);
  }, [currentIndex, matches, updateCurrentHighlight]);

  const handlePrev = useCallback(() => {
    if (matches.length === 0) return;
    const newIndex = (currentIndex - 1 + matches.length) % matches.length;
    updateCurrentHighlight(newIndex);
  }, [currentIndex, matches, updateCurrentHighlight]);

  const handleReplace = useCallback(() => {
    if (matches.length === 0 || currentIndex < 0) return;
    const match = matches[currentIndex];
    editor.chain().focus().insertContentAt({ from: match.from, to: match.to }, replaceTerm).run();
  }, [editor, matches, currentIndex, replaceTerm]);

  const handleReplaceAll = useCallback(() => {
    if (matches.length === 0) return;
    // Replace from end to start to preserve positions
    const sortedMatches = [...matches].sort((a, b) => b.from - a.from);
    const chain = editor.chain().focus();
    for (const match of sortedMatches) {
      chain.insertContentAt({ from: match.from, to: match.to }, replaceTerm);
    }
    chain.run();
  }, [editor, matches, replaceTerm]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "Enter") {
        if (e.shiftKey) {
          handlePrev();
        } else {
          handleNext();
        }
      }
    },
    [onClose, handleNext, handlePrev]
  );

  return (
    <div className="find-replace-bar">
      <div className="find-replace-row">
        <div className="find-replace-input-group">
          <input
            ref={searchInputRef}
            type="text"
            className="find-replace-input"
            placeholder="Find"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button
            type="button"
            className={`find-replace-toggle ${caseSensitive ? "active" : ""}`}
            onClick={() => setCaseSensitive(!caseSensitive)}
            title="Match case"
          >
            Aa
          </button>
          <button
            type="button"
            className={`find-replace-toggle ${wholeWord ? "active" : ""}`}
            onClick={() => setWholeWord(!wholeWord)}
            title="Match whole word"
          >
            <span style={{ textDecoration: "underline" }}>ab</span>
          </button>
          <button
            type="button"
            className={`find-replace-toggle ${useRegex ? "active" : ""}`}
            onClick={() => setUseRegex(!useRegex)}
            title="Use regular expression"
          >
            .*
          </button>
        </div>
        <span className="find-replace-count">
          {regexError
            ? "Bad pattern"
            : matches.length > 0
              ? `${currentIndex + 1} of ${matches.length}`
              : searchTerm
                ? "No results"
                : ""}
        </span>
        <button
          type="button"
          className="find-replace-btn"
          onClick={handlePrev}
          disabled={matches.length === 0}
          title="Previous match (Shift+Enter)"
        >
          <BsChevronUp />
        </button>
        <button
          type="button"
          className="find-replace-btn"
          onClick={handleNext}
          disabled={matches.length === 0}
          title="Next match (Enter)"
        >
          <BsChevronDown />
        </button>
        <button
          type="button"
          className="find-replace-btn find-replace-expand"
          onClick={() => setShowReplace(!showReplace)}
          title={showReplace ? "Hide Replace" : "Show Replace"}
        >
          {showReplace ? "Hide" : "Replace"}
        </button>
        <button
          type="button"
          className="find-replace-btn find-replace-close"
          onClick={onClose}
          title="Close (Escape)"
        >
          <BsX />
        </button>
      </div>
      {showReplace && (
        <div className="find-replace-row">
          <div className="find-replace-input-group">
            <input
              type="text"
              className="find-replace-input"
              placeholder="Replace with"
              value={replaceTerm}
              onChange={(e) => setReplaceTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          <button
            type="button"
            className="find-replace-action-btn"
            onClick={handleReplace}
            disabled={matches.length === 0}
            title="Replace current"
          >
            Replace
          </button>
          <button
            type="button"
            className="find-replace-action-btn"
            onClick={handleReplaceAll}
            disabled={matches.length === 0}
            title="Replace all"
          >
            All
          </button>
        </div>
      )}
    </div>
  );
};

export default FindReplaceBar;
